/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * PLP-only product helpers (rating).
 * Kept in a separate module so cart and other routes don't pull this code.
 * Brand and short description are not derived from search hits here — search API
 * does not expose those fields in a reliable way for tiles.
 */

export type ProductSearchHitLike = {
    productId?: string;
    productName?: string;
    representedProduct?: Record<string, unknown>;
    [key: string]: unknown;
};

function toFinite(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

export function getProductRating(product: ProductSearchHitLike): { rating: number; reviewCount: number } {
    const rep = (product?.representedProduct as Record<string, unknown> | undefined) ?? undefined;
    const rating = toFinite(product?.c_bvAverageRating ?? rep?.c_bvAverageRating);
    const reviewCount = toFinite(product?.c_bvReviewCount ?? rep?.c_bvReviewCount);
    return { rating, reviewCount };
}

/**
 * RGBARRY-CUSTOMIZATION: display labels for the `c_collection` value IDs.
 * SCAPI product-search hits carry the raw value ID (e.g. `manhattan`), not the value's
 * display name. `c_collection` is not a storefront search refinement, so no refinement
 * label is available to resolve it at runtime — the display names live only in the
 * attribute's value definitions in Business Manager. This map mirrors those definitions so
 * the tile can render the human label (e.g. `Manhattan`, `Modern Everywhere`). The labels
 * are not derivable from the ID by casing (`modern` → "Modern Everywhere", `bgactive` → "bg
 * active"), so they are listed explicitly. Keyed by lowercased value ID.
 *
 * The collection value list is small and stable; when merchandising adds or renames a
 * collection value in Business Manager, add the matching entry here. Unmapped IDs fall back
 * to the raw value so a new collection still renders (just without the polished label).
 */
const COLLECTION_LABELS: Record<string, string> = {
    allday: 'All Day',
    classics: 'Classics',
    shearling: 'Shearling',
    footwear: 'Footwear',
    novelty: 'Novelty',
    apparel: 'Apparel',
    home: 'Home',
    athleisure: 'Athleisure',
    modern: 'Modern Everywhere',
    manhattan: 'Manhattan',
    securtex: 'Securtex Anti-Theft',
    bgactive: 'bg active',
    coastal: 'Coastal Flip Lock',
    trueclassic: 'True Classic',
    legacy: 'Legacy',
};

/**
 * Read the merchandising collection name (`c_collection`) off a product search hit and
 * resolve it to its display label.
 *
 * Returned on the hit via the `custom_properties` expand; falls back to the represented
 * product for variant/master hits that carry it there instead. `c_collection` is a
 * multi-value (set-of-string) attribute, so SCAPI serializes it as a string array even with
 * a single value selected — use the first entry.
 *
 * RGBARRY-CUSTOMIZATION: the raw value is a value ID (e.g. `manhattan`); resolve it to its
 * display label via {@link COLLECTION_LABELS}, falling back to the raw value when unmapped.
 * Returns an empty string when the attribute is absent or blank (tile hides the line).
 */
export function getCollectionName(product: ProductSearchHitLike): string {
    const rep = (product?.representedProduct as Record<string, unknown> | undefined) ?? undefined;
    const raw = product?.c_collection ?? rep?.c_collection;

    let value = '';
    if (typeof raw === 'string') {
        value = raw.trim();
    } else if (Array.isArray(raw)) {
        const first = raw.find((v) => typeof v === 'string' && v.trim() !== '');
        value = typeof first === 'string' ? first.trim() : '';
    }

    if (value === '') return '';
    return COLLECTION_LABELS[value.toLowerCase()] ?? value;
}
